import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowLeft, ArrowRight, Phone, CheckCircle2 } from "lucide-react";
import BlogLayout from "../components/BlogLayout";
import Seo from "../components/Seo";
import { BLOG_POSTS } from "../data/blog";
import { CONTACT } from "../data/content";

const fmtDate = (d) => new Date(d).toLocaleDateString("hu-HU", { year: "numeric", month: "long", day: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <BlogLayout>
        <Seo title="A cikk nem található | szuper-szaki.hu" description="A keresett blogbejegyzés nem található." path={`/blog/${slug}`} />
        <div className="max-w-3xl mx-auto px-5 py-28 text-center">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">A cikk nem található</h1>
          <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-sky-700 font-bold"><ArrowLeft className="w-4 h-4" /> Vissza a bloghoz</Link>
        </div>
      </BlogLayout>
    );
  }

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    image: post.cover,
    datePublished: post.date,
    author: { "@type": "Organization", name: "Szuper Szaki" },
    publisher: { "@type": "Organization", name: "szuper-szaki.hu" },
    mainEntityOfPage: `https://szuper-szaki.hu/blog/${post.slug}`,
  };

  return (
    <BlogLayout>
      <Seo title={`${post.title} | szuper-szaki.hu`} description={post.description} path={`/blog/${post.slug}`} image={post.cover} type="article" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <article className="max-w-3xl mx-auto px-5 lg:px-8 py-12 lg:py-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-sky-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Vissza a bloghoz
        </Link>

        <div className="flex items-center gap-3 text-xs font-semibold text-slate-400 mb-4">
          <span className="text-sky-600 bg-sky-50 px-2.5 py-1 rounded-full border border-sky-100">{post.category}</span>
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{fmtDate(post.date)}</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{post.readMin} perc olvasás</span>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">{post.title}</h1>
        <p className="mt-4 text-lg text-slate-600 leading-relaxed">{post.excerpt}</p>

        <div className="mt-8 rounded-2xl overflow-hidden">
          <img src={post.cover} alt={post.coverAlt} className="w-full h-64 sm:h-80 object-cover" />
        </div>

        <div className="mt-10 space-y-8">
          {post.sections.map((s, i) => (
            <motion.section key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}>
              <h2 className="font-display text-2xl font-extrabold text-slate-900 mb-3">{s.h}</h2>
              {s.body.map((para, j) => (
                <p key={j} className="text-slate-700 leading-relaxed mb-3">{para}</p>
              ))}
              {s.list && (
                <ul className="mt-2 space-y-2">
                  {s.list.map((li, k) => (
                    <li key={k} className="flex items-start gap-2 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" /> <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.section>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-3xl bg-slate-950 ss-grain p-8 text-center overflow-hidden relative">
          <div className="absolute -top-16 left-1/3 w-72 h-72 rounded-full bg-sky-600/20 blur-[100px]" />
          <div className="relative">
            <h3 className="font-display text-2xl font-extrabold text-white">Segítségre van szüksége?</h3>
            <p className="mt-2 text-slate-300">A Szuper Szaki Budapesten és Keszthely környékén is gyorsan megoldja a víz- és fűtésszerelési problémákat.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/#arajanlat" className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white px-6 py-3 rounded-full font-bold shadow-lg shadow-sky-500/30 transition-colors">
                Ingyenes árajánlatot kérek <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:${CONTACT.phoneHref}`} className="inline-flex items-center gap-2 border border-white/20 hover:bg-white/10 text-white px-6 py-3 rounded-full font-bold transition-colors">
                <Phone className="w-4 h-4" /> {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* related */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-16">
        <h2 className="font-display text-2xl font-extrabold text-slate-900 mb-6">További cikkek</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {related.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="group block rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-sky-200 transition-all">
              <div className="h-36 overflow-hidden">
                <img src={p.cover} alt={p.coverAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4">
                <h3 className="font-display font-bold text-slate-900 group-hover:text-sky-700 transition-colors leading-snug text-sm">{p.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </BlogLayout>
  );
}
